import { Box, Button, Card, DataTable, EmptySearchResult, InlineStack, Text } from '@shopify/polaris';
import { LabelPrinterIcon } from '@shopify/polaris-icons';
import { useLineItemsTable, usePurchaseOrderLineItems } from '../../utils/hooks';
import { useParams } from 'react-router';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';
import { useEffect, useState, useMemo } from 'react';

import DataTableFilters from './components/DataTableFilters';
import DataTableRows from './components/DataTableRows';
import PrintLabelsModal from './components/PrintLabelsModal';
import '../../styles/PurchaseOrderReceivingDocument.css';

export default function PurchaseOrderLineItemsTable({
    scannedVariants,
    setScannedVariants,
    passedState,
    purchaseOrder,
    submitClicked,
    displayStatus,
    lastAdded,
    productOnPurchaseOrder,
    handleRemove,
    setHasInteracted,
    onCancelEdit,
    onEditingIndexChange,
    setItemEditing,
    setUnsavedEdits,
    hasUnsavedEdits,
    showNavigationModal,
    hasTriedToNavigateRows,
    resetTriedToNavigateForRow,
    markRowTriedToNavigate,
    entryMode,
}) {
    const { poId, rdId } = useParams();
    usePurchaseOrderLineItems(poId, rdId, setScannedVariants, purchaseOrder);

    // Print Labels Modal state
    const [printLabelsModalOpen, setPrintLabelsModalOpen] = useState(false);

    const {
        productFilter,
        setProductFilter,
        vendorStyleFilter,
        setVendorStyleFilter,
        skuFilter,
        setSkuFilter,
        barcodeFilter,
        setBarcodeFilter,
        qtyExpectedFilter,
        setQtyExpectedFilter,
        qtyReceivedToDateFilter,
        setQtyReceivedToDateFilter,
        qtyReceivedFilter,
        setQtyReceivedFilter,
        queryValue,
        setQueryValue,
        sortIndex,
        setSortIndex,
        sortDirection,
        setSortDirection,
        editingIndex,
        editingQty,
        handleEditClick,
        handleQtyChange,
        handleCancelEdit,
        filteredAndSortedVariants,
        handleManualUpdateOfTotal,
    } = useLineItemsTable(scannedVariants, setScannedVariants, setHasInteracted, setItemEditing, setUnsavedEdits, resetTriedToNavigateForRow);

    const guardedSetQueryValue = (value) => {
        if (entryMode === 'scan-mode') return;
        setQueryValue(value);
    };

    useEffect(() => {
        if (entryMode === 'scan-mode' && queryValue !== '') {
            setQueryValue('');
        }
    }, [entryMode, setQueryValue, queryValue]);

    // Prepare variants data for the modal
    const modalVariants = useMemo(() => {
        return filteredAndSortedVariants.map((variant) => ({
            key: variant.variant?.varaintId,
            quantity: variant.quantityReceived || 0,
            sku: variant.variant?.sku,
            productName: variant.variant?.altText || 'Unknown Product',
            amazonSku: variant.variant?.amazonSku || null,
            barcode: variant.variant?.barcode || null,
        }));
    }, [filteredAndSortedVariants]);

    const headings = [
        PURCHASE_ORDER_STRINGS.PRODUCT,
        PURCHASE_ORDER_STRINGS.VENDOR_STYLE,
        PURCHASE_ORDER_STRINGS.SKU,
        PURCHASE_ORDER_STRINGS.UPC,
        PURCHASE_ORDER_STRINGS.QTY_EXPECTED,
        PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_RECEIVING_HEADER.TOTAL_QUANTITY_RECEIVED_ON_THIS_PO,
        PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_RECEIVING_HEADER.QUANTITY_RECEIVED_ON_THIS_DOCUMENT,
        PURCHASE_ORDER_STRINGS.ACTIONS,
    ];

    const rows = DataTableRows({
        variants: filteredAndSortedVariants,
        scannedVariants: scannedVariants,
        editingIndex,
        editingQty,
        handleEditClick,
        handleQtyChange,
        handleCancelEdit,
        passedState,
        submitClicked,
        purchaseOrder,
        poId,
        rdId,
        handleManualUpdateOfTotal,
        displayStatus,
        lastAdded,
        productOnPurchaseOrder,
        handleRemove,
        setItemEditing,
        hasUnsavedEdits,
        showNavigationModal,
        hasTriedToNavigateRows,
        resetTriedToNavigateForRow,
        markRowTriedToNavigate,
    });

    useEffect(() => {
        if (onCancelEdit) {
            onCancelEdit(handleCancelEdit);
        }
    }, [handleCancelEdit, onCancelEdit]);

    useEffect(() => {
        if (onEditingIndexChange) {
            onEditingIndexChange(editingIndex);
        }
    }, [editingIndex, onEditingIndexChange]);

    return (
        <Card>
            <InlineStack align="space-between" gap="200">
                <Text variant="headingLg">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_LINE_ITEMS_TABLE.LINE_ITEMS}</Text>
                <InlineStack>
                    <Button variant="secondary" icon={LabelPrinterIcon} onClick={() => setPrintLabelsModalOpen(true)} disabled={filteredAndSortedVariants.length === 0}>
                        Print Labels For Items
                    </Button>
                </InlineStack>
            </InlineStack>
            <Box paddingBlock="4">
                <DataTableFilters
                    productFilter={productFilter}
                    setProductFilter={setProductFilter}
                    vendorStyleFilter={vendorStyleFilter}
                    setVendorStyleFilter={setVendorStyleFilter}
                    skuFilter={skuFilter}
                    setSkuFilter={setSkuFilter}
                    barcodeFilter={barcodeFilter}
                    setBarcodeFilter={setBarcodeFilter}
                    qtyExpectedFilter={qtyExpectedFilter}
                    setQtyExpectedFilter={setQtyExpectedFilter}
                    qtyReceivedToDateFilter={qtyReceivedToDateFilter}
                    setQtyReceivedToDateFilter={setQtyReceivedToDateFilter}
                    qtyReceivedFilter={qtyReceivedFilter}
                    setQtyReceivedFilter={setQtyReceivedFilter}
                    queryValue={queryValue}
                    setQueryValue={guardedSetQueryValue}
                    isScanMode={entryMode === 'scan-mode'}
                    entryMode={entryMode}
                />

                {rows.length > 0 ? (
                    <div className="purchase-order-line-items-table">
                        <DataTable
                            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                            headings={headings}
                            rows={rows}
                            sortable={[true, true, true, true, true, true, true, false]}
                            sortColumnIndex={sortIndex}
                            sortDirection={sortDirection}
                            onSort={(index, direction) => {
                                setSortIndex(index);
                                setSortDirection(direction);
                            }}
                        />
                    </div>
                ) : (
                    <Box padding="1000">
                        <EmptySearchResult
                            title={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_LINE_ITEMS_TABLE.noItemsFound}
                            description={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_LINE_ITEMS_TABLE.PLEASE_SCAN_FIRST_ITEM}
                            withIllustration
                        />
                    </Box>
                )}
            </Box>

            <PrintLabelsModal open={printLabelsModalOpen} onClose={() => setPrintLabelsModalOpen(false)} variants={modalVariants} />
        </Card>
    );
}
